import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ETICHETTE_AVVISO,
  useAvvisi,
  useChiudiAvviso,
  useChiudiTuttiGliAvvisi,
  useInvii,
  useModelliPosta,
} from '../api/avvisi.js';
import { data, dataOra } from '../formati.js';
import { locale, t } from '../i18n/lingua.js';

/**
 * Gli avvisi: la lista che si guarda la mattina.
 *
 * Uno per ordine. Nel v2 sono 9.938 righe di cui solo 3.378 diverse, e su un
 * ordine solo ce ne sono cinquemila che dicono tutte la stessa cosa: quando
 * una lista fa così, si smette di guardarla.
 *
 * Accanto agli avvisi c'è la posta, perché la domanda arriva sempre in
 * coppia: «l'arrivo si è spostato?» e «il cliente l'ha saputo?».
 */

const SCHEDE = [
  { chiave: 'avvisi', titolo: t('Avvisi') },
  { chiave: 'posta', titolo: t('Posta in uscita') },
  { chiave: 'modelli', titolo: t('Modelli') },
] as const;

type Scheda = (typeof SCHEDE)[number]['chiave'];

export function Avvisi() {
  const avvisi = useAvvisi();
  const invii = useInvii();
  const modelli = useModelliPosta();
  const chiudi = useChiudiAvviso();
  const chiudiTutti = useChiudiTuttiGliAvvisi();
  const [scheda, setScheda] = useState<Scheda>('avvisi');
  const [chiediConferma, setChiediConferma] = useState(false);

  const daRileggere = (modelli.data?.templates ?? []).filter((m) => !m.reviewed).length;
  const aperti = avvisi.data?.total ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Avvisi')}</h1>
          <div className="page-sub">
            {avvisi.data
              ? avvisi.data.total === 0
                ? t('Niente da guardare.')
                : (avvisi.data.total === 1
                    ? t('1 avviso aperto')
                    : t('{n} avvisi aperti', { n: avvisi.data.total.toLocaleString(locale()) })) +
                  // se ne mostrano cento: va detto, se no sembrano tutti
                  (avvisi.data.alerts.length < avvisi.data.total
                    ? t(': qui i primi {n}', { n: avvisi.data.alerts.length })
                    : '')
              : avvisi.error
                ? t('Gli avvisi non sono arrivati: riprova fra un momento.')
                : t('Un momento…')}
          </div>
        </div>
        {scheda === 'avvisi' && aperti > 0 && avvisi.data?.canCloseAll && (
          <div className="page-actions">
            <button
              className="btn"
              onClick={() => setChiediConferma(true)}
              disabled={chiudiTutti.isPending}
            >
              {t('Chiudi tutti')}
            </button>
          </div>
        )}
      </div>

      {/* La conferma: chiudere un avviso alla volta si rifà a mano, chiuderne
          tremila no. Dice quanti sono, perché è il numero che fa decidere. */}
      {chiediConferma && (
        <div className="bulkbar bulkbar-attenzione" role="alertdialog">
          <span className="avviso-segno">▲</span>
          <strong>{t('Chiudo i {n} avvisi aperti?', { n: aperti.toLocaleString('it-IT') })}</strong>
          <span className="dim">
            {t('Restano a storico con la data di oggi. Se un arrivo si sposta ancora, l’avviso ricompare.')}
          </span>
          <span style={{ marginLeft: 'auto' }} />
          <button
            className="btn"
            onClick={() => setChiediConferma(false)}
            disabled={chiudiTutti.isPending}
          >
            {t('Annulla')}
          </button>
          <button
            className="btn btn-pericolo"
            disabled={chiudiTutti.isPending}
            onClick={() =>
              chiudiTutti.mutate(undefined, { onSuccess: () => setChiediConferma(false) })
            }
          >
            {chiudiTutti.isPending ? 'Chiudo…' : t('Sì, chiudili')}
          </button>
        </div>
      )}

      {chiudiTutti.isSuccess && !chiediConferma && (
        <div className="nota-prezzo" style={{ marginBottom: 12 }}>
          {chiudiTutti.data.closed === 1
            ? t('Chiuso un avviso.')
            : t('Chiusi {n} avvisi.', { n: chiudiTutti.data.closed.toLocaleString('it-IT') })}
        </div>
      )}

      <div className="tabs">
        {SCHEDE.map((s) => (
          <button
            key={s.chiave}
            className={scheda === s.chiave ? 'tab tab-on' : 'tab'}
            onClick={() => setScheda(s.chiave)}
          >
            {s.titolo}
            {s.chiave === 'avvisi' && (avvisi.data?.total ?? 0) > 0 && (
              <span className="count">{avvisi.data!.total}</span>
            )}
            {s.chiave === 'modelli' && daRileggere > 0 && (
              <span className="count">{daRileggere}</span>
            )}
          </button>
        ))}
      </div>

      {scheda === 'avvisi' && (
        <div className="table-wrap a-schede sezione-avvisi">
          <table className="tabella">
            <thead>
              <tr>
                <th>{t('Ordine')}</th>
                <th>{t('Cliente')}</th>
                <th>{t('Che cosa')}</th>
                <th>{t('Arrivo previsto')}</th>
                <th className="destra">{t('Rivisto')}</th>
                <th>{t('Notificato')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(avvisi.data?.alerts ?? []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="dim" style={{ textAlign: 'center', padding: 18 }}>
                    {avvisi.isLoading
                      ? t('Un momento…')
                      : avvisi.error
                        ? // un errore del servizio non è «nessun avviso»
                          t('Gli avvisi non sono arrivati: riprova fra un momento.')
                        : t('Nessun avviso aperto.')}
                  </td>
                </tr>
              ) : (
                avvisi.data!.alerts.map((a) => (
                  <tr key={a.id}>
                    <td className="cella-titolo">
                      <Link
                        className="cell-link strong"
                        to="/sales-orders/$id"
                        params={{ id: String(a.orderId) }}
                      >
                        {a.orderNumber}
                      </Link>
                    </td>
                    <td data-etichetta={t('Cliente')}>{a.customer}</td>
                    <td data-etichetta={t('Che cosa')}>
                      <span className="badge badge-warn">
                        {ETICHETTE_AVVISO[a.type] ?? a.type}
                      </span>
                    </td>
                    <td className="num" data-etichetta={t('Arrivo previsto')}>
                      {a.previousEstimatedArrivalDate && a.currentEstimatedArrivalDate ? (
                        <>
                          {data(a.previousEstimatedArrivalDate)}
                          <span className="dim"> → </span>
                          <strong>{data(a.currentEstimatedArrivalDate)}</strong>
                        </>
                      ) : (
                        <span className="dim">—</span>
                      )}
                    </td>
                    <td className="destra num" data-etichetta={t('Rivisto')}>
                      {/* quante volte lo stesso scostamento si è ripresentato:
                          se sale, vuol dire che qualcosa non si aggiorna */}
                      {a.seenCount > 1 ? (
                        <strong title={t('Ultima volta {quando}', { quando: dataOra(a.lastSeenAt) })}>
                          {t('{n} volte', { n: a.seenCount })}
                        </strong>
                      ) : (
                        <span className="dim">{t('una volta')}</span>
                      )}
                    </td>
                    <td className="dim num" data-etichetta={t('Notificato')}>
                      {a.notifiedAt ? dataOra(a.notifiedAt) : <span className="dim">{t('no')}</span>}
                    </td>
                    <td className="destra">
                      <button
                        className="btn btn-ghost"
                        disabled={chiudi.isPending}
                        onClick={() => chiudi.mutate(a.id)}
                      >
                        {t('Chiudi')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {scheda === 'posta' && (
        <div className="sezione-avvisi">
          {invii.data && !invii.data.sending && (
            <div className="nota-prezzo" style={{ marginBottom: 12 }}>
              {t('La posta non è ancora collegata: i messaggi vengono composti e registrati qui, ma non partono. Serve la chiave di Resend.')}
            </div>
          )}

          <div className="table-wrap">
            <table className="tabella">
              <thead>
                <tr>
                  <th>{t('Quando')}</th>
                  <th>{t('Messaggio')}</th>
                  <th>A</th>
                  <th>{t('Esito')}</th>
                  <th>{t('Problema')}</th>
                </tr>
              </thead>
              <tbody>
                {(invii.data?.deliveries ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="dim" style={{ textAlign: 'center', padding: 18 }}>
                      {t('Nessun messaggio ancora.')}
                    </td>
                  </tr>
                ) : (
                  invii.data!.deliveries.map((d) => (
                    <tr key={d.id}>
                      <td className="num">{dataOra(d.sentAt)}</td>
                      <td>{d.templateAlias}</td>
                      <td>{d.recipient}</td>
                      <td>
                        <span className={d.succeeded ? 'badge badge-ok' : 'badge badge-alert'}>
                          {d.succeeded ? 'partito' : 'non partito'}
                        </span>
                      </td>
                      <td className="dim" style={{ maxWidth: 340 }}>
                        {d.error ?? ''}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {scheda === 'modelli' && (
        <div className="sezione-avvisi">
          {daRileggere > 0 && (
            <div className="nota-prezzo" style={{ marginBottom: 12 }}>
              {t('{n} testi non sono ancora stati riletti da una persona. Quelli inglesi del vecchio programma sono passati da Google Translate — «Employment Desidered» viene da lì.', {
                n: daRileggere,
              })}
            </div>
          )}

          <div className="table-wrap">
            <table className="tabella">
              <thead>
                <tr>
                  <th>{t('Modello')}</th>
                  <th>{t('Lingua')}</th>
                  <th>{t('Oggetto')}</th>
                  <th>{t('Riletto')}</th>
                </tr>
              </thead>
              <tbody>
                {(modelli.data?.templates ?? []).map((m) => (
                  <tr key={`${m.alias}-${m.language}`}>
                    <td>{m.alias}</td>
                    <td>
                      <span className="badge">{m.language.toUpperCase()}</span>
                    </td>
                    <td>{m.subject}</td>
                    <td>
                      {m.reviewed ? (
                        <span className="badge badge-ok">{t('sì')}</span>
                      ) : (
                        <span className="badge badge-warn">{t('no')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
